<template>
  <Teleport to="body">
    <!-- Acima do TaskDetailModal (z-[10000]) e dos seletores embutidos (z-[12050]) -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[12080] flex items-center justify-center bg-black/50 backdrop-blur-[1px]"
      @keydown.escape.window="closeModal"
    >
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col" @click.stop>
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-secondary)]">
        <div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ t('kanban.userSelector.title') }}</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('kanban.userSelector.subtitle') }}</p>
        </div>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Search -->
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <input 
            v-model="searchQuery"
            type="text" 
            :placeholder="t('kanban.userSelector.searchPlaceholder')" 
            class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
        </div>
      </div>

      <!-- Users List -->
      <div class="flex-1 min-h-0 overflow-y-auto p-6">
        <div class="space-y-3">
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            @click="toggleUser(user)"
            class="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-all duration-200"
            :class="selectedUsers.includes(user.id) ? 'bg-blue-50 dark:bg-blue-900 border-blue-300 dark:border-blue-600' : ''"
            @mouseenter="console.log('🔍 User hover:', user.nomeCompleto, 'ID:', user.id, 'fotoPerfil:', user.fotoPerfil, 'Selected:', selectedUsers.includes(user.id))"
          >
            <div class="flex items-center space-x-4">
              <div class="relative">
                <!-- Foto do perfil se disponível -->
                <img 
                  v-if="user.fotoPerfil && user.fotoPerfil.trim()" 
                  :src="user.fotoPerfil" 
                  :alt="user.nomeCompleto"
                  class="w-12 h-12 rounded-full border-2 border-white dark:border-gray-700 object-cover hover:scale-110 transition-all duration-300"
                  @error="handleImageError"
                />
                <!-- Iniciais se não houver foto -->
                <div 
                  v-else 
                  class="initials-fallback w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                >
                  {{ getInitials(user.nomeCompleto) }}
                </div>
                <div v-if="user.online" class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900 dark:text-white">{{ user.nomeCompleto }}</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">{{ user.email }}</p>
                <div v-if="user.cargo" class="flex items-center space-x-2 mt-1">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"></path>
                  </svg>
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ user.cargo }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-3">
              <div v-if="selectedUsers.includes(user.id)" class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div v-else class="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 rounded-full"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredUsers.length === 0" class="text-center py-12">
          <svg class="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">{{ t('kanban.userSelector.emptyTitle') }}</h3>
          <p class="text-gray-500 dark:text-gray-400">{{ t('kanban.userSelector.emptyHint') }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          {{ t('kanban.userSelector.selectedCount', { count: selectedUsers.length }) }}
        </div>
        <div class="flex space-x-3">
          <button @click="closeModal" class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors inline-flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            {{ t('kanban.userSelector.cancel') }}
          </button>
          <button @click="confirmSelection" class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium inline-flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            {{ t('kanban.userSelector.confirm') }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import { useWorkspaceStore } from '../stores/workspace'

const { t } = useI18n()
const route = useRoute()
const workspaceStore = useWorkspaceStore()

const resolveWorkspaceId = () => {
  const fromRoute = route.params?.id
  if (fromRoute != null && fromRoute !== '') {
    return Number(fromRoute)
  }
  return workspaceStore.currentWorkspace?.id ?? null
}

const props = defineProps({
  isOpen: Boolean,
  selectedUsers: {
    type: Array,
    default: () => []
  },
  availableUsers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'confirm'])

// State
const searchQuery = ref('')
const users = ref([])
const selectedUsers = ref([...props.selectedUsers])

// Computed
const filteredUsers = computed(() => {
  // Usar availableUsers se fornecidos, senão usar users
  const userList = props.availableUsers.length > 0 ? props.availableUsers : users.value
  
  if (!searchQuery.value) return userList
  
  const query = searchQuery.value.toLowerCase()
  return userList.filter(user => 
    user.nomeCompleto.toLowerCase().includes(query) ||
    user.email.toLowerCase().includes(query) ||
    (user.cargo && user.cargo.toLowerCase().includes(query))
  )
})

// Methods
const closeModal = () => {
  emit('close')
}

const confirmSelection = () => {
  emit('confirm', selectedUsers.value)
  closeModal()
}

const toggleUser = (user) => {
  console.log('🔄 ToggleUser: user.id =', user.id, typeof user.id)
  console.log('🔄 ToggleUser: selectedUsers =', selectedUsers.value)
  
  const index = selectedUsers.value.indexOf(user.id)
  console.log('🔄 ToggleUser: index =', index)
  
  if (index > -1) {
    selectedUsers.value.splice(index, 1)
    console.log('➖ UserSelectorModal: Removendo usuário:', user.nomeCompleto)
  } else {
    selectedUsers.value.push(user.id)
    console.log('➕ UserSelectorModal: Adicionando usuário:', user.nomeCompleto)
  }
}

const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

const handleImageError = (event) => {
  // Se a imagem falhar ao carregar, esconder a img e mostrar as iniciais
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  const initialsDiv = parent.querySelector('.initials-fallback')
  if (initialsDiv) {
    initialsDiv.style.display = 'flex'
  }
}

const loadUsers = async () => {
  try {
    const workspaceId = resolveWorkspaceId()

    if (workspaceId) {
      console.log('🔍 Carregando usuários do workspace:', workspaceId)

      const response = await api.get(`/workspace-acesso/workspace/${workspaceId}`)
      
      // Extrair dados dos usuários da resposta
      users.value = response.data.map(acesso => ({
        id: acesso.usuarioId,
        nomeCompleto: acesso.usuarioNome,
        email: acesso.usuarioEmail,
        fotoPerfil: acesso.usuarioFotoPerfil,
        roleWorkspace: acesso.roleWorkspace,
        ativo: acesso.ativo,
        online: Math.random() > 0.5 // Mock online status
      }))
      
      console.log('👥 Usuários carregados:', users.value.length)
      console.log('🔍 Dados dos usuários:', users.value)
    } else {
      console.warn('⚠️ Workspace não identificado na rota nem no store')
      users.value = []
    }
  } catch (error) {
    console.error('❌ Erro ao carregar usuários do workspace:', error)
    users.value = []
  }
}

// Watch for modal open
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    selectedUsers.value = [...props.selectedUsers]
    console.log('🔍 UserSelectorModal: IDs pré-selecionados:', selectedUsers.value)
    loadUsers()
  }
})

// Watch for users loading to re-apply selection
watch(() => users.value, (newUsers) => {
  if (newUsers && newUsers.length > 0 && props.selectedUsers.length > 0) {
    console.log('👥 UserSelectorModal: Usuários carregados, reaplicando seleção')
    console.log('👥 Usuários disponíveis:', newUsers.map(u => ({ id: u.id, nome: u.nomeCompleto })))
    console.log('👥 IDs para selecionar:', props.selectedUsers)
    
    // Reaplicar seleção após carregar usuários
    selectedUsers.value = [...props.selectedUsers]
    console.log('✅ UserSelectorModal: Seleção reaplicada:', selectedUsers.value)
  }
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
